import React from "react";
import {
  makeStyles,
  Typography,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  Tab,
  Tabs,
} from "@material-ui/core";
import { colors } from "theme/colors";
import * as R from "ramda";
import { ExpandMoreOutlined } from "@material-ui/icons";
import { readGoalSettingReviewForm } from "components/listOfValues/utils";
import { geAnnualPeriod } from "../../academicPlan/utils";
import { useDispatch } from "react-redux";
import { updateForm } from "state/slices/globalFormSlice";
import { FORM_TYPES } from "common/utils/formUtility";
import { useSelector } from "react-redux";
import { PAGES, updatePageListItems } from "state/slices/pageSlice";
import SetGoalsReadView from "./SetGoalsReadView";
import GoalFeedback from "../setup/GoalFeedback";

const useStyles = makeStyles((theme) => ({
  title: {
    marginBottom: "0.75rem",
  },
  heading: {
    fontSize: theme.typography.pxToRem(18),
    fontWeight: theme.typography.fontWeightBold,
  },
  subHeading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
  },
  editIconContainer: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-end",
  },
  editIconBtn: {
    padding: "0px",
  },
  editIcon: {
    height: "1.3rem",
    width: "1.3rem",
    fontSize: "1.3rem",
    color: "rgba(0, 0, 0, 0.54)",
  },
  label: {
    color: colors.gray4,
  },
  activeLabel: {
    color: colors.green,
  },
  deactivateLabel: {
    color: colors.red,
  },
  formContainer: {
    marginBottom: "0.5rem",
  },
  pointer: {
    display: "flex",
    alignItems: "center",
  },
  accordionDetails: {
    display: "inline",
    padding: "1rem",
    height: "auto",
    paddingLeft: "1rem",
    paddingRight: "1rem",
  },
  outcomeList: {
    height: "auto",
    width: "100%",
  },
  outcomeAccordionContainer: {
    overflow: "auto",
  },
  observationRegistryHeader: {
    backgroundColor: "#1b2945",
    color: colors.gray1,
    borderRadius: 6,
    paddingBottom: "0.5rem",
  },
  topIcon: {
    color: colors.gray1,
  },
  chip1: {
    color: "#fff",
    border: "1px solid #fff",
    fontWeight: 300,
    backgroundColor: "#263657",
  },
  subUnitChip: {
    "&.MuiChip-root": {
      marginRight: "1rem",
      color: colors.blue1,
      fontSize: "16px",
      fontWeight: "500",
    },
  },
}));

export const goalSettingReviewFormState = {
  PENDING_REVIEW: "Review Pending",
  SENT_FOR_REASSESSMENT: "Sent For Reassessment",
  APPROVED: "Approved",
  SELF_REVIEW_COMPLETED: "Self Review Completed",
  PEER_REVIEW_COMPLETED: "Peer Review Completed",
  FINAL_REVIEW_COMPLETED: "Final Review Completed",
};

export const goalSettingReviewFormStateOrder = {
  PENDING_REVIEW: 0,
  SENT_FOR_REASSESSMENT: 1,
  APPROVED: 2,
  SELF_REVIEW_COMPLETED: 3,
  PEER_REVIEW_COMPLETED: 4,
  FINAL_REVIEW_COMPLETED: 5,
};

const a11yProps = (index) => {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
};

const TabPanel = (props) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
};

const ReviewSetGoalListView = ({ data }) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const annualPeriodName = geAnnualPeriod(R.pathOr("", ["annualPeriod"], data));
  const userProfile = useSelector((state) => state.userProfile.userProfile);
  const allRoles = useSelector((state) => state.pages.roles.items);

  const [expanded, setExpanded] = React.useState(false);
  const [openedReviewForm, setOpenedReviewForm] = React.useState("");
  const [selectedTab, setSelectedTab] = React.useState(0);

  const handleChange = (panel) => (event, isExpanded) => {
    isExpanded ? setOpenedReviewForm(data.id) : setOpenedReviewForm(null);
    setExpanded(isExpanded ? panel : false);
  };

  const handleChangeTab = (event, newValue) => {
    setSelectedTab(newValue);
  };
  React.useEffect(() => {
    async function fetchData() {
      // You can await here
      const response = await readGoalSettingReviewForm(openedReviewForm);
      dispatch(
        updatePageListItems({
          page: PAGES.reviewSetGoal,
          itemObject: response,
        })
      );
      // ...
    }
    if (openedReviewForm) fetchData();
  }, [openedReviewForm]);

  return (
    <Grid item xs={12}>
      <div style={{ marginBottom: "1rem" }}>
        <Accordion
          expanded={expanded === "panel1"}
          onChange={handleChange("panel1")}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreOutlined className={classes.topIcon} />}
            aria-controls="panel1a-content"
            id="panel1a-header"
            className={classes.observationRegistryHeader}
          >
            <Grid container xs={12}>
              <Grid item xs={12} md={11}>
                <Typography variant="subtitle1">
                  Goal For: {R.pathOr("-", ["reviewedFor"], data)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={1}>
                <Chip
                  className={classes.chip1}
                  size="small"
                  label={annualPeriodName}
                />
              </Grid>

              <Grid item container style={{ marginTop: "1rem" }}>
                <Grid item xs={6} md={3}>
                  <Typography>
                    State:{" "}
                    {
                      goalSettingReviewFormState[
                      R.pathOr("-", ["reviewState"], data)
                      ]
                    }
                  </Typography>
                </Grid>
                <Grid item xs={8} md={4}>
                  <Typography>
                    Role:{" "}
                    {allRoles.find(
                      (role) =>
                        role.id === R.propOr("", "reviewedForRole", data)
                    )?.name ?? "-"}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </AccordionSummary>
          <AccordionDetails className={classes.accordionDetails}>
            <Box
              sx={{
                paddingLeft: "1rem",
                paddingRight: "1rem",
              }}
            >
              <Box style={{ marginBottom: "1rem" }}>
                <Tabs
                  value={selectedTab}
                  onChange={handleChangeTab}
                  aria-label="basic tabs example"
                >
                  <Tab label="Goal Review" {...a11yProps(0)} />
                  <Tab
                    label="Goal Feedback"
                    {...a11yProps(1)}
                    disabled={
                      goalSettingReviewFormStateOrder[data.reviewState] <
                      goalSettingReviewFormStateOrder["APPROVED"]
                    }
                  />
                </Tabs>
              </Box>
              <TabPanel value={selectedTab} index={0}>
                <SetGoalsReadView goalSetting={data} />
              </TabPanel>
              <TabPanel value={selectedTab} index={1}>
                <GoalFeedback goalSetupId={data.goalSetting?.id} isPeer />
              </TabPanel>
            </Box>
          </AccordionDetails>
        </Accordion>
      </div>
    </Grid>
  );
};

export default ReviewSetGoalListView;
